import { Container } from "@/types/v2";

/**
 * Validates the configuration of a specific container by checking for conflicts with other containers
 * based on linked technicians and linked labels.
 * @param {Container} container - The container to be checked for configuration conflicts.
 * @param {Container[]} containers - The array of all containers used to check against potential conflicts.
 * @returns {{ valid: boolean, message: string }} An object indicating if the container's configuration is valid.
 * The `valid` field is a boolean indicating the status and the `message` field provides details in case of a conflict.
 */
export function checkContainerConfiguration(
  container: Container,
  containers: Container[]
): { valid: boolean; message: string } {
  const containerTechs = container.linkedTechnicianId
    ? typeof container.linkedTechnicianId === "string" ||
      typeof container.linkedTechnicianId === "number"
      ? [`${container.linkedTechnicianId}`]
      : container.linkedTechnicianId
    : [];

  const containerLabels = container.linkedSourceLabel
    ? typeof container.linkedSourceLabel === "string"
      ? [container.linkedSourceLabel]
      : container.linkedSourceLabel
    : [];

  const otherContainers = containers.filter((c) => c.id !== container.id);

  if (containerTechs.length > 0 && containerLabels.length === 0) {
    // Linked on container techs only
    const otherLinkedContainers = otherContainers.filter(
      (c) =>
        normalizeTechs(c.linkedTechnicianId).some((t) =>
          containerTechs.includes(t)
        ) && normalizeLabels(c.linkedSourceLabel).length === 0
    );
    if (otherLinkedContainers.length > 0) {
      return {
        valid: false,
        message: `Container ${container.displayName ?? container.id} has the same linked technician(s) as container ${otherLinkedContainers[0].displayName ?? otherLinkedContainers[0].id}.`,
      };
    }
  } else if (containerTechs.length === 0 && containerLabels.length > 0) {
    // Linked on container labels only
    const otherLinkedContainers = otherContainers.filter(
      (c) =>
        normalizeLabels(c.linkedSourceLabel).some((l) =>
          containerLabels.includes(l)
        ) && normalizeTechs(c.linkedTechnicianId).length === 0
    );
    if (otherLinkedContainers.length > 0) {
      return {
        valid: false,
        message: `Container ${container.displayName ?? container.id} has the same linked label(s) as container ${otherLinkedContainers[0].displayName ?? otherLinkedContainers[0].id}.`,
      };
    }
  } else if (containerTechs.length > 0 && containerLabels.length > 0) {
    // Linked on container techs and labels/
    const otherLinkedContainers = otherContainers.filter(
      (c) =>
        normalizeLabels(c.linkedSourceLabel).some((l) =>
          containerLabels.includes(l)
        ) &&
        normalizeTechs(c.linkedTechnicianId).some((t) =>
          containerTechs.includes(t)
        )
    );
    if (otherLinkedContainers.length > 0) {
      return {
        valid: false,
        message: `Container ${container.displayName ?? container.id} has the same linked label(s) and technician(s) as container ${otherLinkedContainers[0].displayName ?? otherLinkedContainers[0].id}.`,
      };
    }
  }

  return { valid: true, message: "" };
}

/**
 * Normalizes the provided technician IDs into an array of strings.
 * @param {Container["linkedTechnicianId"]} techs - The technician IDs to normalize. Can be a string, number, or an array of such values.
 * @returns {string[]} An array of string representations of the technician IDs.
 */
export function normalizeTechs(
  techs: Container["linkedTechnicianId"]
): string[] {
  return techs
    ? typeof techs === "string" || typeof techs === "number"
      ? [`${techs}`]
      : techs
    : [];
}

/**
 * The single source of truth for "which technicians on this container are
 * eligible to write back to Tekmetric." `container.bidirectionalTechnicianId`
 * is a subset of `linkedTechnicianId` -- when it is `undefined`, every
 * linked technician is eligible (the pre-existing, backward-compatible
 * behavior); once explicitly set, it is authoritative, even if empty. The
 * subset invariant is enforced here (not just assumed): an explicit
 * `bidirectionalTechnicianId` is intersected against `linkedTechnicianId`,
 * so a stale id left over from hand-edited or migrated data can never leak
 * through as write-back-eligible for a technician that isn't even linked.
 * @param {Container | undefined} container - The container to check.
 * @returns {string[]} The write-back-eligible technician ids, normalized to strings.
 */
export function getBidirectionalTechnicianIds(
  container: Container | undefined
): string[] {
  if (!container) return [];
  if (container.bidirectionalTechnicianId === undefined) {
    return normalizeTechs(container.linkedTechnicianId);
  }
  const linkedTechnicianIds = normalizeTechs(container.linkedTechnicianId);
  return normalizeTechs(container.bidirectionalTechnicianId).filter((id) =>
    linkedTechnicianIds.includes(id)
  );
}

/**
 * Normalizes the input labels into an array of strings.
 * @param {Container["linkedSourceLabel"]} labels - The input labels which can be a string, an array of strings, or undefined.
 * @returns {string[]} An array of strings containing the normalized labels. If the input is a string, it is converted to an array with a single element. If the input is undefined, returns an empty array.
 */
export function normalizeLabels(
  labels: Container["linkedSourceLabel"]
): string[] {
  return labels ? (typeof labels === "string" ? [labels] : labels) : [];
}
